"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useMeasurementsFetcher = void 0;
const react_1 = require("react");
const fixedStreamSelectors_1 = require("../../../../store/fixedStreamSelectors");
const fixedStreamSlice_1 = require("../../../../store/fixedStreamSlice");
const hooks_1 = require("../../../../store/hooks");
const mobileStreamSlice_1 = require("../../../../store/mobileStreamSlice");
const timeRanges_1 = require("../../../../utils/timeRanges");
const updateRangeDisplay_1 = require("./updateRangeDisplay");
const INITIAL_EDGE_FETCH_MONTHS = 1;
const MILLISECONDS_IN_TWO_SECONDS = 2 * timeRanges_1.MILLISECONDS_IN_A_SECOND;
const useMeasurementsFetcher = (streamId, sessionStartTime, sessionEndTime, chartComponentRef, fixedSessionTypeSelected, rangeDisplayRef, sensorName) => {
    const isCurrentlyFetchingRef = (0, react_1.useRef)(false);
    const dispatch = (0, hooks_1.useAppDispatch)();
    const isFirstRender = (0, react_1.useRef)(true);
    const fetchAttemptsRef = (0, react_1.useRef)(0);
    const lastFetchTriggerRef = (0, react_1.useRef)(null);
    const pendingSetExtremesRef = (0, react_1.useRef)(null);
    const activeFetchesRef = (0, react_1.useRef)(0);
    const loadingTimeoutRef = (0, react_1.useRef)(null);
    const lastFetchedRangeRef = (0, react_1.useRef)(null);
    const lastUpdateTimeRef = (0, react_1.useRef)(0);
    const fetchedTimeRanges = (0, hooks_1.useAppSelector)((state) => streamId ? (0, fixedStreamSelectors_1.selectFetchedTimeRanges)(state, streamId) : []);
    const findMissingRanges = (start, end) => {
        if (!fetchedTimeRanges.length) {
            if (end - start > timeRanges_1.MILLISECONDS_IN_A_MONTH) {
                const chunks = [];
                let chunkEnd = end;
                while (chunkEnd > start) {
                    const chunkStart = Math.max(start, chunkEnd - timeRanges_1.MILLISECONDS_IN_A_MONTH);
                    chunks.push({
                        start: chunkStart,
                        end: chunkEnd,
                    });
                    chunkEnd = chunkStart;
                }
                return chunks;
            }
            return [{ start, end }];
        }
        const sortedRanges = [...fetchedTimeRanges].sort((a, b) => a.start - b.start);
        const mergedRanges = [];
        if (sortedRanges.length > 0) {
            let currentRange = Object.assign({}, sortedRanges[0]);
            for (let i = 1; i < sortedRanges.length; i++) {
                const nextRange = sortedRanges[i];
                if (nextRange.start <= currentRange.end + timeRanges_1.MILLISECONDS_IN_A_MINUTE) {
                    currentRange.end = Math.max(currentRange.end, nextRange.end);
                }
                else {
                    mergedRanges.push(currentRange);
                    currentRange = Object.assign({}, nextRange);
                }
            }
            mergedRanges.push(currentRange);
        }
        const gaps = [];
        let currentStart = start;
        for (const range of mergedRanges) {
            if (currentStart < range.start) {
                const gapEnd = Math.min(range.start, end);
                if (gapEnd - currentStart > timeRanges_1.MILLISECONDS_IN_A_MINUTE * 5) {
                    gaps.push({
                        start: currentStart,
                        end: gapEnd,
                    });
                }
            }
            currentStart = Math.max(currentStart, range.end);
        }
        if (currentStart < end) {
            if (end - currentStart > timeRanges_1.MILLISECONDS_IN_A_MINUTE) {
                gaps.push({
                    start: currentStart,
                    end,
                });
            }
        }
        return gaps.reduce((acc, gap) => {
            if (gap.end - gap.start > timeRanges_1.MILLISECONDS_IN_A_MONTH) {
                let chunkEnd = gap.end;
                while (chunkEnd > gap.start) {
                    const chunkStart = Math.max(gap.start, chunkEnd - timeRanges_1.MILLISECONDS_IN_A_MONTH);
                    acc.push({
                        start: chunkStart,
                        end: chunkEnd,
                    });
                    chunkEnd = chunkStart;
                }
            }
            else {
                acc.push(gap);
            }
            return acc;
        }, []);
    };
    const updateExtremesAndDisplay = (start, end, trigger) => {
        var _a;
        if ((_a = chartComponentRef === null || chartComponentRef === void 0 ? void 0 : chartComponentRef.current) === null || _a === void 0 ? void 0 : _a.chart) {
            const chart = chartComponentRef.current.chart;
            const effectiveTrigger = trigger && trigger !== "none"
                ? trigger
                : lastFetchTriggerRef.current || "none";
            chart.xAxis[0].setExtremes(start, end, true, false, {
                trigger: effectiveTrigger,
            });
            (0, updateRangeDisplay_1.updateRangeDisplay)(rangeDisplayRef, start, end, effectiveTrigger === "calendarDay");
        }
    };
    const updateLoadingState = (increment) => {
        var _a;
        if (increment) {
            activeFetchesRef.current++;
            if ((_a = chartComponentRef === null || chartComponentRef === void 0 ? void 0 : chartComponentRef.current) === null || _a === void 0 ? void 0 : _a.chart) {
                chartComponentRef.current.chart.showLoading("Loading data from server...");
            }
        }
        else {
            activeFetchesRef.current = Math.max(0, activeFetchesRef.current - 1);
            if (activeFetchesRef.current === 0) {
                // Clear any existing timeout
                if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                }
                // Set a new timeout to hide loading indicator after a delay
                loadingTimeoutRef.current = setTimeout(() => {
                    var _a;
                    if ((_a = chartComponentRef === null || chartComponentRef === void 0 ? void 0 : chartComponentRef.current) === null || _a === void 0 ? void 0 : _a.chart) {
                        chartComponentRef.current.chart.hideLoading();
                    }
                    loadingTimeoutRef.current = null;
                }, 500);
            }
        }
    };
    const fetchMeasurementsIfNeeded = (start, end, isEdgeFetch = false, isDaySelection = false, trigger) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!streamId || isCurrentlyFetchingRef.current) {
            return;
        }
        // Guard against invalid time ranges
        if (start >= end || end - start < 1000) {
            return;
        }
        // Check if this exact range was just fetched recently
        const now = Date.now();
        if (lastFetchedRangeRef.current &&
            lastFetchedRangeRef.current.start === start &&
            lastFetchedRangeRef.current.end === end &&
            now - lastUpdateTimeRef.current < 500 &&
            trigger !== "initial") {
            return;
        }
        const boundedStart = Math.max(start, sessionStartTime);
        const boundedEnd = Math.min(end, sessionEndTime);
        // Additional guard for bounded range
        if (boundedStart >= boundedEnd) {
            return;
        }
        // Store this range as the last fetched range
        lastFetchedRangeRef.current = { start, end };
        lastUpdateTimeRef.current = now;
        // Add this specific check for the "allButtonClicked" trigger
        const isAllButtonClick = trigger === "allButtonClicked";
        // Special handling for "all" data request
        const totalSessionDuration = sessionEndTime - sessionStartTime;
        const isAllDataRequest = isAllButtonClick ||
            (trigger === "rangeSelectorButton" &&
                ((boundedEnd - boundedStart > timeRanges_1.MILLISECONDS_IN_A_MONTH / 2 &&
                    boundedStart <= sessionStartTime + timeRanges_1.MILLISECONDS_IN_A_DAY &&
                    boundedEnd >= sessionEndTime - timeRanges_1.MILLISECONDS_IN_A_DAY) ||
                    boundedEnd - boundedStart > totalSessionDuration * 0.9)) ||
            (trigger === "navigator" &&
                boundedEnd - boundedStart > totalSessionDuration * 0.9);
        // Force fetch for initial loads and "all" data requests
        const shouldForceFetch = trigger === "initial" || isAllDataRequest || isAllButtonClick;
        // Find missing ranges first
        const missingRanges = findMissingRanges(boundedStart, boundedEnd);
        // Only show loading indicator if we have missing ranges to fetch or it's a forced fetch
        const shouldShowLoading = missingRanges.length > 0 || shouldForceFetch;
        if (!shouldShowLoading && !shouldForceFetch) {
            return;
        }
        try {
            isCurrentlyFetchingRef.current = true;
            if (shouldShowLoading) {
                updateLoadingState(true);
            }
            if (trigger === "initial" && !fixedSessionTypeSelected) {
                const result = yield dispatch((0, fixedStreamSlice_1.fetchMeasurements)({
                    streamId: Number(streamId),
                    startTime: Math.floor(boundedStart).toString(),
                    endTime: Math.floor(boundedEnd).toString(),
                    sensorName,
                })).unwrap();
                if (result && result.length > 0) {
                    dispatch((0, fixedStreamSlice_1.updateFetchedTimeRanges)({
                        streamId,
                        start: boundedStart,
                        end: boundedEnd,
                    }));
                    if (fixedSessionTypeSelected) {
                        dispatch((0, fixedStreamSlice_1.updateFixedMeasurementExtremes)({
                            streamId,
                            min: boundedStart,
                            max: boundedEnd,
                        }));
                    }
                    else {
                        dispatch((0, mobileStreamSlice_1.updateMobileMeasurementExtremes)({
                            min: boundedStart,
                            max: boundedEnd,
                        }));
                    }
                }
            }
            else {
                // Special handling for "all" data request - fetch entire session in chunks
                if (isAllDataRequest || isAllButtonClick) {
                    if (isAllButtonClick) {
                        try {
                            // Show a clear loading state
                            if ((_a = chartComponentRef === null || chartComponentRef === void 0 ? void 0 : chartComponentRef.current) === null || _a === void 0 ? void 0 : _a.chart) {
                                chartComponentRef.current.chart.showLoading("Loading all data...");
                            }
                            // First, clear any existing tracked ranges for this stream
                            // This ensures we'll fetch everything fresh
                            if (streamId) {
                                dispatch({
                                    type: "fixedStream/clearFetchedTimeRanges",
                                    payload: streamId,
                                });
                            }
                            // Then fetch all data in chunks to ensure complete coverage
                            const allRanges = [];
                            let chunkStart = sessionStartTime;
                            // Create chunks of reasonable size
                            while (chunkStart < sessionEndTime) {
                                const chunkEnd = Math.min(chunkStart + timeRanges_1.MILLISECONDS_IN_A_MONTH, sessionEndTime);
                                allRanges.push({
                                    start: chunkStart,
                                    end: chunkEnd,
                                });
                                chunkStart = chunkEnd;
                            }
                            // Fetch all data sequentially to ensure order
                            for (let i = 0; i < allRanges.length; i++) {
                                const range = allRanges[i];
                                const result = yield dispatch((0, fixedStreamSlice_1.fetchMeasurements)({
                                    streamId: Number(streamId),
                                    startTime: Math.floor(range.start).toString(),
                                    endTime: Math.floor(range.end).toString(),
                                    sensorName,
                                })).unwrap();
                                if (result && result.length > 0) {
                                    dispatch((0, fixedStreamSlice_1.updateFetchedTimeRanges)({
                                        streamId,
                                        start: range.start,
                                        end: range.end,
                                    }));
                                }
                            }
                            // After fetching all data, update measurement extremes for the entire session
                            if (fixedSessionTypeSelected) {
                                dispatch((0, fixedStreamSlice_1.updateFixedMeasurementExtremes)({
                                    streamId,
                                    min: sessionStartTime,
                                    max: sessionEndTime,
                                }));
                            }
                            else {
                                dispatch((0, mobileStreamSlice_1.updateMobileMeasurementExtremes)({
                                    min: sessionStartTime,
                                    max: sessionEndTime,
                                }));
                            }
                            // After fetching all data, force extremes update to show everything
                            if ((_b = chartComponentRef === null || chartComponentRef === void 0 ? void 0 : chartComponentRef.current) === null || _b === void 0 ? void 0 : _b.chart) {
                                chartComponentRef.current.chart.xAxis[0].setExtremes(sessionStartTime, sessionEndTime, true, false);
                            }
                            // Skip the regular fetch logic since we've handled everything
                            return;
                        }
                        catch (error) { }
                    }
                }
                const visibleRange = boundedEnd - boundedStart;
                const fetchPadding = Math.min(visibleRange * 0.25, // 25% of the visible range
                timeRanges_1.MILLISECONDS_IN_A_DAY);
                const paddedStart = Math.max(sessionStartTime, boundedStart - fetchPadding);
                const paddedEnd = Math.min(sessionEndTime, boundedEnd + fetchPadding);
                const missingRanges = findMissingRanges(paddedStart, paddedEnd);
                if (missingRanges.length === 0) {
                    if (pendingSetExtremesRef.current) {
                        const { start, end } = pendingSetExtremesRef.current;
                        updateExtremesAndDisplay(start, end, lastFetchTriggerRef.current || undefined);
                    }
                    return;
                }
                // Process all ranges in parallel for efficiency
                const fetchPromises = missingRanges.map((range, index) => __awaiter(void 0, void 0, void 0, function* () {
                    let fetchStart = range.start;
                    let fetchEnd = range.end;
                    if (isDaySelection) {
                        const paddedStart = Math.max(sessionStartTime, fetchStart - timeRanges_1.MILLISECONDS_IN_A_DAY * 2);
                        const paddedEnd = Math.min(sessionEndTime, fetchEnd + timeRanges_1.MILLISECONDS_IN_A_DAY - MILLISECONDS_IN_TWO_SECONDS);
                        if (paddedEnd - paddedStart > timeRanges_1.MILLISECONDS_IN_A_MONTH) {
                            fetchStart = paddedEnd - timeRanges_1.MILLISECONDS_IN_A_MONTH;
                            fetchEnd = paddedEnd;
                        }
                        else {
                            fetchStart = paddedStart;
                            fetchEnd = paddedEnd;
                        }
                    }
                    else if (isEdgeFetch) {
                        const baseRange = Math.min(timeRanges_1.MILLISECONDS_IN_A_MONTH * INITIAL_EDGE_FETCH_MONTHS, timeRanges_1.MILLISECONDS_IN_A_MONTH);
                        const expansionFactor = Math.min(Math.pow(2, fetchAttemptsRef.current), timeRanges_1.MILLISECONDS_IN_A_MONTH / baseRange);
                        const totalRange = Math.min(baseRange * expansionFactor, timeRanges_1.MILLISECONDS_IN_A_MONTH);
                        fetchStart = Math.max(sessionStartTime, fetchStart - totalRange / 2);
                        fetchEnd = Math.min(sessionEndTime, fetchEnd + totalRange / 2);
                    }
                    if (fetchEnd - fetchStart > timeRanges_1.MILLISECONDS_IN_A_MONTH) {
                        fetchStart = fetchEnd - timeRanges_1.MILLISECONDS_IN_A_MONTH;
                    }
                    const result = yield dispatch((0, fixedStreamSlice_1.fetchMeasurements)({
                        streamId: Number(streamId),
                        startTime: Math.floor(fetchStart).toString(),
                        endTime: Math.floor(fetchEnd).toString(),
                        sensorName,
                    })).unwrap();
                    if (result && result.length > 0) {
                        dispatch((0, fixedStreamSlice_1.updateFetchedTimeRanges)({
                            streamId,
                            start: fetchStart,
                            end: fetchEnd,
                        }));
                    }
                    return { fetchStart, fetchEnd, resultLength: (result === null || result === void 0 ? void 0 : result.length) || 0 };
                }));
                // Wait for all fetches to complete before updating loading state
                if (fetchPromises.length > 0) {
                    try {
                        yield Promise.all(fetchPromises);
                        // Update measurement extremes after fetches complete
                        if (streamId) {
                            if (fixedSessionTypeSelected) {
                                dispatch((0, fixedStreamSlice_1.updateFixedMeasurementExtremes)({
                                    streamId,
                                    min: boundedStart,
                                    max: boundedEnd,
                                }));
                            }
                            else {
                                dispatch((0, mobileStreamSlice_1.updateMobileMeasurementExtremes)({
                                    min: boundedStart,
                                    max: boundedEnd,
                                }));
                            }
                        }
                    }
                    catch (error) { }
                }
                if (pendingSetExtremesRef.current) {
                    const { start, end } = pendingSetExtremesRef.current;
                    requestAnimationFrame(() => {
                        updateExtremesAndDisplay(start, end, lastFetchTriggerRef.current || undefined);
                    });
                }
                if (isFirstRender.current) {
                    isFirstRender.current = false;
                }
            }
        }
        catch (error) {
            console.error("Error in fetchMeasurementsIfNeeded", error);
        }
        finally {
            isCurrentlyFetchingRef.current = false;
            pendingSetExtremesRef.current = null;
            if (shouldShowLoading) {
                updateLoadingState(false);
            }
        }
    });
    return { fetchMeasurementsIfNeeded };
};
exports.useMeasurementsFetcher = useMeasurementsFetcher;
